import useAuthService from 'api/services/authService'
import usePropertyService from 'api/services/propertyService'
import useUserService from 'api/services/userService'
import { getAuthUserSubject } from 'api/utils/AuthManager'
import PageLoading from 'components/common/loading/PageLoading'
import MySpaceProfileCard from 'components/myAccount/MySpaceProfileCard'
import {
  getFirstRegisteredProperty,
  hasMeaningfulAddress,
  propertyAddressToAddressData,
  resolveProfileAddress,
} from 'components/myAccount/mySpaceUtils'
import { AddressData } from 'newTypes/location'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'

function asProfileRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object') return null
  return value as Record<string, unknown>
}

const ProfilePage = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { loadUser, updateUser } = useUserService()
  const { loadMyProperties } = usePropertyService()
  const { isAuthenticated, user: authUser, token } = useAuthService()
  const currentUserId = getAuthUserSubject(authUser)
  const canFetch = isAuthenticated && Boolean(currentUserId) && Boolean(token)

  const [userData, setUserData] = useState<Record<string, unknown> | null>(null)
  const [initialData, setInitialData] = useState<Record<string, unknown> | null>(
    null
  )
  const [isEditing, setIsEditing] = useState(false)

  const {
    data: fetchedUser,
    isLoading: isLoadingUser,
    isFetching: isFetchingUser,
  } = useQuery(['loadUser', currentUserId], () => loadUser(), {
    enabled: canFetch,
  })

  const { mutateAsync: updateUserMutation, isLoading: isUpdating } =
    useMutation(updateUser)

  const { data: myProperties } = useQuery(
    ['myProperties', currentUserId],
    loadMyProperties,
    { enabled: canFetch }
  )

  const firstPropertyAddress = useMemo((): AddressData | null => {
    const firstListing = getFirstRegisteredProperty(myProperties)
    return propertyAddressToAddressData(firstListing?.address)
  }, [myProperties])

  // Keep local form state in sync with the server profile (cache or network).
  // Do not overwrite while the user is editing.
  useEffect(() => {
    if (isEditing) return
    const next = asProfileRecord(fetchedUser) ?? asProfileRecord(authUser)
    if (!next) return
    setUserData(next)
    setInitialData(next)
  }, [fetchedUser, authUser, isEditing])

  // When entering edit (or when the first listing loads mid-edit), prefill empty address.
  useEffect(() => {
    if (!isEditing || !firstPropertyAddress) return
    setUserData(prev => {
      if (!prev || hasMeaningfulAddress(prev.address)) return prev
      return { ...prev, address: firstPropertyAddress }
    })
  }, [isEditing, firstPropertyAddress])

  const profileUserData = useMemo(() => {
    if (!userData) {
      const fallback = asProfileRecord(fetchedUser) ?? asProfileRecord(authUser)
      if (!fallback) return null
      if (isEditing) return fallback
      const resolvedAddress = resolveProfileAddress(
        null,
        fallback.address,
        firstPropertyAddress
      )
      return {
        ...fallback,
        address: resolvedAddress ?? fallback.address,
      }
    }

    if (isEditing) return userData

    const resolvedAddress = resolveProfileAddress(
      initialData?.address,
      userData.address,
      firstPropertyAddress
    )
    return {
      ...userData,
      address: resolvedAddress ?? userData.address,
    }
  }, [
    userData,
    fetchedUser,
    authUser,
    initialData?.address,
    firstPropertyAddress,
    isEditing,
  ])

  const isProfileLoading =
    canFetch && !profileUserData && (isLoadingUser || isFetchingUser)

  const handleChange = (e: { name?: string; value?: unknown }) => {
    const field = e?.name
    if (!isEditing || !field || field === 'email') return

    setUserData(prev => {
      const base =
        prev ??
        asProfileRecord(fetchedUser) ??
        asProfileRecord(authUser) ??
        null
      if (!base) return prev
      return { ...base, [field]: e.value }
    })
  }

  const handleEditProfile = () => {
    setUserData(prev => {
      const base =
        prev ??
        asProfileRecord(fetchedUser) ??
        asProfileRecord(authUser) ??
        null
      if (!base) return prev

      const resolved = resolveProfileAddress(
        initialData?.address ?? base.address,
        base.address,
        firstPropertyAddress
      )
      if (!resolved || hasMeaningfulAddress(base.address)) {
        return base
      }
      return { ...base, address: resolved }
    })
    setIsEditing(true)
  }

  const persistProfile = async (payload: Record<string, unknown>) => {
    const lockedEmail =
      (typeof initialData?.email === 'string' && initialData.email) ||
      (typeof payload.email === 'string' && payload.email) ||
      ''

    const response = await updateUserMutation({
      ...payload,
      id: payload.id ?? currentUserId,
      email: lockedEmail,
      address: payload.address ?? null,
    })

    if (response.success) {
      const nextData = response.data
        ? { ...payload, ...response.data, email: lockedEmail }
        : { ...payload, email: lockedEmail }
      setUserData(nextData)
      setInitialData(nextData)
      queryClient.setQueryData(['loadUser', currentUserId], nextData)
      setIsEditing(false)
      toast.success(String(t('account.mySpace.profileSaved')))
    } else {
      toast.error(String(t('account.mySpace.profileSaveFailed')))
    }
  }

  const handleSave = () => {
    if (!userData && !profileUserData) return
    // Commit typed address (onBlur) before reading state for save.
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
    window.setTimeout(() => {
      setUserData(current => {
        const payload =
          current ??
          asProfileRecord(profileUserData) ??
          asProfileRecord(fetchedUser)
        if (!payload) return current
        if (JSON.stringify(payload) !== JSON.stringify(initialData)) {
          void persistProfile(payload)
        } else {
          setIsEditing(false)
        }
        return payload
      })
    }, 0)
  }

  const handleCancel = () => {
    setUserData(initialData)
    setIsEditing(false)
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-imovWhiteShadow to-white py-6 md:py-10">
      <div className="mx-auto max-w-3xl px-4">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm font-medium text-imovPrimary">
              <Link
                to="/account"
                className="hover:underline focus:outline-none focus-visible:underline"
              >
                ← {t('account.mySpace.pageTitle')}
              </Link>
            </p>
            <h1 className="mt-1 text-2xl font-bold tracking-tight text-imovSecondary sm:text-3xl">
              {t('common.menu.profile')}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              {t('account.mySpace.profilePageSubtitle')}
            </p>
          </div>
        </div>

        {isProfileLoading ? (
          <PageLoading minHeightClassName="min-h-[calc(100vh-220px)]" />
        ) : (
          <MySpaceProfileCard
            userData={profileUserData}
            isEditing={isEditing}
            isSaving={isUpdating}
            onEdit={handleEditProfile}
            onCancel={handleCancel}
            onSave={handleSave}
            onChange={handleChange}
          />
        )}
      </div>
    </div>
  )
}

export default ProfilePage
